/**
 * Conversions and validation between the cadence the user builds in the wizard
 * (`CadenceDraft`) and the API's `scheduleSpec`. Kept out of the components so
 * the create wizard, the edit dialog and the preview call all derive the spec
 * exactly the same way.
 */

import { getLocalTimeZone, isoToLocalDateTime, localDateTimeToISO } from '~/utils/date';

import type { ScheduleSpecWire } from '../types/catalog-ops-schedule.api.types';
import type { CadenceDraft, IntervalUnitSeconds } from '../types/catalog-ops-schedule.types';

/** `MONDAY`…`SUNDAY` in the order the API and a week both use. */
export const DAYS_OF_WEEK = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;

/** Shortest interval the form allows. The API accepts smaller values, but a
 *  catalog import that runs more often than every 5 minutes will always be
 *  overlapping itself. */
const MIN_INTERVAL_SECONDS = 300;

/** Validation outcomes, mapped to copy by the component via
 *  `t('schedules.cadence.errors.<code>')` so no user-facing string lives here. */
export type CadenceErrorCode =
  | 'CRON_FIELDS'
  | 'INTERVAL_TOO_SHORT'
  | 'HOUR_RANGE'
  | 'MINUTE_RANGE'
  | 'DAY_OF_MONTH'
  | 'END_BEFORE_START'
  | 'JITTER_NEGATIVE';

/** A weekly Monday 02:00 schedule in the viewer's own zone — the cadence most
 *  catalog batches actually want, and a safe starting point to edit down from. */
export function defaultCadenceDraft(): CadenceDraft {
  return {
    mode: 'WEEKLY',
    hour: '2',
    minute: '0',
    dayOfWeek: 'MONDAY',
    dayOfMonth: '1',
    intervalValue: '6',
    intervalUnit: 3600,
    cron: '0 2 * * 1',
    timezone: getLocalTimeZone(),
    startAt: '',
    endAt: '',
    jitterSeconds: '',
  };
}

/** Total seconds an INTERVAL draft represents. `intervalValue` is a raw string;
 *  an empty/non-numeric value yields `NaN`, which `validateCadence` rejects. */
export function intervalSeconds(draft: CadenceDraft): number {
  return Math.round(Number(draft.intervalValue) * draft.intervalUnit);
}

/**
 * Builds the API spec from the draft. Exactly one of cron/calendar/interval is
 * populated to match `type` — sending more than one is a 400.
 */
export function cadenceDraftToSpec(draft: CadenceDraft): ScheduleSpecWire {
  const spec: ScheduleSpecWire = { type: 'CALENDAR', timezone: draft.timezone };

  if (draft.mode === 'CRON') {
    spec.type = 'CRON';
    spec.cron = draft.cron.trim();
  } else if (draft.mode === 'INTERVAL') {
    spec.type = 'INTERVAL';
    spec.interval = { everySeconds: intervalSeconds(draft), offsetSeconds: 0 };
  } else {
    // Coerced here, not in the form: the draft holds raw strings and this runs
    // only on a draft `validateCadence` has already accepted.
    spec.calendar = {
      dayOfWeek: draft.mode === 'WEEKLY' ? draft.dayOfWeek : null,
      dayOfMonth: draft.mode === 'MONTHLY' ? Number(draft.dayOfMonth) : null,
      month: null,
      hour: Number(draft.hour),
      minute: Number(draft.minute),
    };
  }

  // Bounds are picked as wall-clock times against the schedule's own zone, so
  // they're converted with that zone rather than the viewer's.
  if (draft.startAt) spec.startAt = localDateTimeToISO(draft.startAt, draft.timezone);
  if (draft.endAt) spec.endAt = localDateTimeToISO(draft.endAt, draft.timezone);
  if (draft.jitterSeconds !== '') spec.jitterSeconds = Number(draft.jitterSeconds);

  return spec;
}

/** Picks the interval unit that renders `everySeconds` most readably —
 *  86400 reads as "1 day", not "1440 minutes". */
function splitInterval(everySeconds: number): {
  value: number;
  unit: IntervalUnitSeconds;
} {
  if (everySeconds > 0 && everySeconds % 86400 === 0) {
    return { value: everySeconds / 86400, unit: 86400 };
  }
  if (everySeconds > 0 && everySeconds % 3600 === 0) {
    return { value: everySeconds / 3600, unit: 3600 };
  }
  return { value: Math.max(1, Math.round(everySeconds / 60)), unit: 60 };
}

/**
 * Round-trips a stored spec back into the form model, so editing a schedule
 * opens on its current cadence. A CALENDAR spec resolves to DAILY / WEEKLY /
 * MONTHLY by which of its day fields are populated.
 */
export function specToCadenceDraft(spec: ScheduleSpecWire): CadenceDraft {
  const draft = defaultCadenceDraft();
  draft.timezone = spec.timezone || draft.timezone;

  if (spec.type === 'CRON') {
    draft.mode = 'CRON';
    draft.cron = spec.cron ?? draft.cron;
  } else if (spec.type === 'INTERVAL') {
    draft.mode = 'INTERVAL';
    const { value, unit } = splitInterval(spec.interval?.everySeconds ?? 86400);
    draft.intervalValue = String(value);
    draft.intervalUnit = unit;
  } else {
    const cal = spec.calendar;
    draft.hour = cal?.hour != null ? String(cal.hour) : draft.hour;
    draft.minute = cal?.minute != null ? String(cal.minute) : draft.minute;
    if (cal?.dayOfWeek) {
      draft.mode = 'WEEKLY';
      draft.dayOfWeek = cal.dayOfWeek;
    } else if (cal?.dayOfMonth) {
      draft.mode = 'MONTHLY';
      draft.dayOfMonth = String(cal.dayOfMonth);
    } else {
      draft.mode = 'DAILY';
    }
  }

  draft.startAt = spec.startAt ? isoToLocalDateTime(spec.startAt, draft.timezone) : '';
  draft.endAt = spec.endAt ? isoToLocalDateTime(spec.endAt, draft.timezone) : '';
  draft.jitterSeconds =
    spec.jitterSeconds === null || spec.jitterSeconds === undefined
      ? ''
      : String(spec.jitterSeconds);

  return draft;
}

/** Shallow shape check on a 5-field unix cron. The backend is the authority —
 *  this only catches the obvious mistakes before a round trip. */
function isCronShaped(expression: string): boolean {
  const fields = expression.trim().split(/\s+/);
  if (fields.length !== 5) return false;
  return fields.every((f) => /^[0-9*,\-/]+$/.test(f));
}

/**
 * Client-side cadence validation — enough to keep "Continue" honest without a
 * round trip. Returns `null` when the draft is submittable.
 */
export function validateCadence(draft: CadenceDraft): CadenceErrorCode | null {
  if (draft.mode === 'CRON') {
    if (!isCronShaped(draft.cron)) return 'CRON_FIELDS';
  } else if (draft.mode === 'INTERVAL') {
    // Empty / non-numeric parses to NaN, which every comparison below is false
    // for — reject it explicitly so a cleared field can't slip through.
    const value = Number(draft.intervalValue);
    if (!Number.isInteger(value) || value < 1) return 'INTERVAL_TOO_SHORT';
    if (intervalSeconds(draft) < MIN_INTERVAL_SECONDS) return 'INTERVAL_TOO_SHORT';
  } else {
    const hour = Number(draft.hour);
    const minute = Number(draft.minute);
    // The raw string can be '' or non-numeric mid-edit; treat that as out of
    // range so Continue stays blocked until a real value is entered.
    if (draft.hour.trim() === '' || !Number.isInteger(hour) || hour < 0 || hour > 23) {
      return 'HOUR_RANGE';
    }
    if (draft.minute.trim() === '' || !Number.isInteger(minute) || minute < 0 || minute > 59) {
      return 'MINUTE_RANGE';
    }
    if (draft.mode === 'MONTHLY') {
      const dayOfMonth = Number(draft.dayOfMonth);
      if (
        draft.dayOfMonth.trim() === '' ||
        !Number.isInteger(dayOfMonth) ||
        dayOfMonth < 1 ||
        dayOfMonth > 31
      ) {
        return 'DAY_OF_MONTH';
      }
    }
  }
  if (draft.startAt && draft.endAt && draft.endAt <= draft.startAt) return 'END_BEFORE_START';
  if (draft.jitterSeconds !== '' && Number(draft.jitterSeconds) < 0) return 'JITTER_NEGATIVE';
  return null;
}

/** Two-digit clock padding for cadence labels. */
function pad(value: number): string {
  return value.toString().padStart(2, '0');
}

/** Title-cases a `MONDAY`-style enum for display. */
export function formatDayOfWeek(day: string): string {
  return day.charAt(0) + day.slice(1).toLowerCase();
}

/**
 * Local stand-in for the server's `cadenceLabel`, used before the schedule
 * exists (the review step). Once a schedule is created, always render the
 * server's own label instead of re-deriving it here.
 */
export function describeCadenceDraft(draft: CadenceDraft): string {
  const at = `${pad(Number(draft.hour))}:${pad(Number(draft.minute))}`;
  switch (draft.mode) {
    case 'DAILY':
      return `Every day · ${at}`;
    case 'WEEKLY':
      return `Every ${formatDayOfWeek(draft.dayOfWeek)} · ${at}`;
    case 'MONTHLY':
      return `Day ${draft.dayOfMonth} of each month · ${at}`;
    case 'INTERVAL': {
      const unit =
        draft.intervalUnit === 60 ? 'minute' : draft.intervalUnit === 3600 ? 'hour' : 'day';
      const plural = Number(draft.intervalValue) === 1 ? '' : 's';
      return `Every ${draft.intervalValue} ${unit}${plural}`;
    }
    case 'CRON':
      return `Cron · ${draft.cron.trim()}`;
  }
}
